import styled from '@emotion/styled';
import React, {
  useState,
  useLayoutEffect,
  useMemo,
  Ref,
  useRef,
  useEffect,
  useImperativeHandle,
} from 'react';
import { useMemoizedFn, useSafeState, useTrackedEffect } from 'ahooks';
import { noop, throttle } from 'lodash-es';
import Message from './components/Message';
import { DoubleRightOutlined } from '@ant-design/icons';
import { useTranslate } from '@tolgee/react';
import { eventBus } from 'services/eventBus';
import { Anchor } from './Anchor';
import { HighlightMsg } from './HighlightMsg';
import { cx } from '@emotion/css';
import { QAPair } from './components/QAPair';
import { MsgListItem } from './components/MsgListItem';

import type { DialogueMessage, DialogueProps, DialogueRefObject } from './types';
import { getMetaFromSender } from 'views/Conversation/Chat/utils';
import { useContextMenu } from 'react-contexify';
import { contextMenuId } from 'views/Conversation/Chat/components/MessageMenu/ContextMenu';

const Dialogue = <M extends DialogueMessage = DialogueMessage>(
  props: DialogueProps<M>,
  ref: Ref<DialogueRefObject>,
) => {
  const {
    className,
    header = null,
    footer = null,
    anchorExtra = null,
    messages: messagesProp = [],
    messageRender,
    pos: posProp,
    preventPosScroll = false,
    promoteHumanMsgId,
    conversation,
    highlightMsg,
    isGalaxy,
    ...otherProps
  } = props;

  const { t } = useTranslate();

  const [promoteHumanMsgAnchorVisible, setPromoteHumanMsgAnchorVisible] = useState<boolean>();
  const [lastMsgAnchorVisible, setLastMsgAnchorVisible] = useSafeState(false);
  const [promoteHumanMsgHighlightKey, setPromoteHumanMsgHighlightKey] = useState(0);
  const [highlightMsgId, setHighlightMsgId] = useState<string>();
  const [highlightKey, setHighlightKey] = useState(0);
  const dialogueContainerRef = useRef<HTMLDivElement | null>(null);

  const [pos, setPos] = useState(posProp);
  const messagesRef = useRef<(HTMLDivElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [autoScrolling, setAutoScrolling] = useSafeState(false);
  const autoScrollRef = useRef(autoScrolling);
  const messageIdMapIndexRef = useRef(
    {} as {
      [x: string]: number;
    },
  );
  const manualScrollRef = useRef(false);
  const posRef = useRef(pos);
  const messages = useMemo(() => {
    const messageIdMapIndex = {} as {
      [x: string]: number;
    };
    messageIdMapIndexRef.current = messageIdMapIndex;
    const content = messagesProp.map((message, idx) => {
      messageIdMapIndex[message.dialogueUuid] = idx;
      // 消息分配需要合并， 只展示最后一条消息
      if (
        message.raw?.messageType === 'SYS_ASSIGN_CONTEXT' &&
        messagesProp[idx + 1]?.raw?.messageType === 'SYS_ASSIGN_CONTEXT'
      ) {
        return null;
      }
      const msgContent = messageRender(message, idx);
      if (!msgContent) {
        return null;
      }
      const { layout } = getMetaFromSender(message.sender);

      if (message.dialogueUuid === promoteHumanMsgId) {
        return (
          <HighlightMsg
            key={message.raw.msgId}
            highlightKey={promoteHumanMsgHighlightKey}
            getRef={(ref) => (messagesRef.current[idx] = ref.current || null)}
            onVisibleChange={
              promoteHumanMsgAnchorVisible === false
                ? undefined
                : (visible) => setPromoteHumanMsgAnchorVisible(!visible)
            }
          >
            <MsgListItem
              isGalaxy={isGalaxy}
              layout={layout}
              key={message.raw.msgId}
              conversation={conversation}
              message={message.raw}
            >
              {msgContent}
            </MsgListItem>
          </HighlightMsg>
        );
      }
      if (message.dialogueUuid === highlightMsgId) {
        return (
          <HighlightMsg
            key={message.raw.msgId}
            highlightKey={highlightKey}
            getRef={(ref) => (messagesRef.current[idx] = ref.current || null)}
          >
            <MsgListItem
              isGalaxy={isGalaxy}
              layout={layout}
              key={message.raw.msgId}
              conversation={conversation}
              message={message.raw}
            >
              {msgContent}
            </MsgListItem>
          </HighlightMsg>
        );
      }

      return (
        <MsgListItem
          isGalaxy={isGalaxy}
          layout={layout}
          key={message.raw.msgId}
          conversation={conversation}
          message={message.raw}
          highlightMsg={highlightMsg}
          getRef={(ref) => (messagesRef.current[idx] = ref.current || null)}
        >
          {msgContent}
        </MsgListItem>
      );
    });
    return content;
  }, [
    messagesProp,
    messageRender,
    promoteHumanMsgId,
    highlightMsgId,
    isGalaxy,
    conversation,
    highlightMsg,
    promoteHumanMsgHighlightKey,
    promoteHumanMsgAnchorVisible,
    highlightKey,
  ]);

  useEffect(() => {
    posRef.current = pos;
    manualScrollRef.current = false;
  }, [pos]);

  useEffect(() => {
    posRef.current = posProp;
    setPos(posProp);
  }, [posProp]);

  useEffect(() => {
    autoScrollRef.current = false;
  }, [autoScrolling]);

  const scollingEffect = useMemoizedFn(
    throttle(
      () => {
        if (preventPosScroll) {
          return;
        }

        setAutoScrolling(true);
        const { current: dom } = containerRef;
        const { current: pos } = posRef;
        const func = () => {
          if (!dom || manualScrollRef.current) {
            return;
          }
          if (typeof pos === 'number') {
            dom.scrollTop = pos;
            return;
          }
          if (pos === 'bottom') {
            const px = dom.scrollHeight;
            dom.scrollTop = px;
          } else if (pos === 'top') {
            dom.scrollTop = 0;
          } else if (pos?.uuid) {
            const idx = messageIdMapIndexRef.current[pos.uuid];
            const messagebox = messagesRef.current[idx];
            if (messagebox) {
              messagebox.scrollIntoView(pos?.options);
            }
          }
        };
        func();
        setAutoScrolling(false);
      },
      400,
      { trailing: true, leading: true },
    ),
  );
  useEffect(() => {
    if (!containerRef.current) {
      return noop;
    }
    const obcb = () => {
      scollingEffect();
    };
    const rob = new ResizeObserver(obcb);
    const ob = new MutationObserver(obcb);
    rob.observe(containerRef.current);
    ob.observe(containerRef.current, { subtree: true, childList: true });
    return () => {
      ob.disconnect();
      rob.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(scollingEffect, [messages, pos]);
  useLayoutEffect(() => {
    const dialogEl = containerRef.current;
    if (dialogEl) {
      let holding = false;
      const mouseup = () => {
        holding = false;
      };
      const mousedown = () => {
        holding = true;
      };
      const scrolled = throttle((e: any) => {
        if (autoScrollRef.current) {
          e.preventDefault();
        }

        const { scrollTop, offsetHeight, scrollHeight } = dialogEl;

        setLastMsgAnchorVisible(scrollHeight - scrollTop > 3 * offsetHeight);

        if (messagesRef.current.length && (e.type === 'wheel' || holding)) {
          manualScrollRef.current = true;
        }
      }, 100);
      dialogEl.addEventListener('wheel', scrolled);
      dialogEl.addEventListener('scroll', scrolled);
      dialogEl.addEventListener('mousedown', mousedown);
      document.addEventListener('mouseup', mouseup);
      document.addEventListener('visibilitychange', mouseup);
      document.addEventListener('blur', mouseup);
      return () => {
        dialogEl.removeEventListener('scroll', scrolled);
        dialogEl.removeEventListener('wheel', scrolled);
        dialogEl.removeEventListener('mousedown', mousedown);
        document.removeEventListener('mouseup', mouseup);
        document.removeEventListener('visibilitychange', mouseup);
        document.removeEventListener('blur', mouseup);
      };
    }
    return noop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 新消息来时，是否需要滚动到底部
  useTrackedEffect(
    (_, [preMessagesLength] = [], [curMessagesLength] = []) => {
      if (
        !preMessagesLength ||
        !curMessagesLength ||
        curMessagesLength - preMessagesLength !== 1 ||
        !containerRef.current
      ) {
        return;
      }

      const { scrollTop, offsetHeight, scrollHeight } = containerRef.current;

      const lastMessage = messagesRef.current[messagesRef.current.length - 1];

      if (scrollTop + offsetHeight + (lastMessage?.clientHeight || 0) > scrollHeight - 80) {
        requestAnimationFrame(() => {
          containerRef.current?.scrollTo({ top: Number.MAX_SAFE_INTEGER, behavior: 'smooth' });
        });
      }
    },
    [messagesProp.length],
  );

  // 使用 useMemo 保持 ref 暴露对象引用稳定，方法内通过 ref 访问最新值
  const dialogueRefObject: DialogueRefObject = useMemo(
    () => ({
      get dom() {
        return containerRef.current;
      },
      scrollToMessage: (uuid, options) => {
        setPos({ uuid, options });
      },
      scrollTo: (pos: 'top' | 'bottom' | number) => {
        if (containerRef.current) {
          let px = typeof pos === 'number' ? pos : 0;
          if (pos === 'bottom') {
            px = containerRef.current.scrollHeight;
          }
          containerRef.current.scrollTop = px;
          setPos(pos);
        }
      },
      scrollToMessageByScrollTo: (uuid, options, manualScroll = false) => {
        const { [uuid]: index } = messageIdMapIndexRef.current;
        const { [index]: message } = messagesRef.current;
        const { current: dialogue } = containerRef;
        if (!message || !dialogue) {
          return false;
        }
        const { offsetTop } = message;
        /**
         * @attention 不能替换为scrollIntoView
         * @attention 因为scrollIntoView会导致所有父级滚动，但部分界面不应该令到父级滚动。
         */
        dialogue.scroll({
          top: offsetTop,
          ...options,
        });
        if (manualScroll) {
          manualScrollRef.current = true;
        }
        return true;
      },
    }),
    [], // ref/setPos 稳定，无需依赖
  );

  useImperativeHandle(ref, () => dialogueRefObject);

  useEffect(() => {
    const handler = (msgId: string) => {
      setHighlightMsgId(msgId);
      setHighlightKey((key) => key + 1);
      dialogueRefObject.scrollToMessageByScrollTo(msgId, { behavior: 'smooth' });
      manualScrollRef.current = true;
    };

    eventBus.on('SCROLL_TO_MSG', handler);

    return () => eventBus.off('SCROLL_TO_MSG', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const anchor = (
    <div className="flex flex-col items-end absolute bottom-[4px] right-[16px]">
      {anchorExtra}
      {promoteHumanMsgAnchorVisible && promoteHumanMsgId ? (
        <Anchor
          onClick={() => {
            setPromoteHumanMsgHighlightKey(1);
            dialogueRefObject.scrollToMessageByScrollTo(promoteHumanMsgId, { behavior: 'smooth' });
            manualScrollRef.current = true;
          }}
        >
          <DoubleRightOutlined style={{ transform: 'rotate(-90deg)' }} />
          <span>{t('aide.有待处理消息')}</span>
        </Anchor>
      ) : lastMsgAnchorVisible ? (
        <Anchor
          onClick={() => {
            containerRef.current?.scrollTo({
              top: containerRef.current.scrollHeight,
              behavior: 'smooth',
            });
            manualScrollRef.current = true;
          }}
        >
          <DoubleRightOutlined style={{ transform: 'rotate(90deg)' }} />
          <span>{t('aide.最新消息')}</span>
        </Anchor>
      ) : null}
    </div>
  );

  const { hideAll } = useContextMenu({
    id: contextMenuId,
  });

  const hideAllPersist = useMemoizedFn(
    throttle(() => {
      hideAll();
    }, 300),
  );

  return (
    <div
      ref={dialogueContainerRef}
      className={cx('relative overflow-hidden', className)}
      {...otherProps}
    >
      <Container onScroll={hideAllPersist} ref={containerRef}>
        {header}
        {messages}
        {footer}
      </Container>
      {anchor}
      {!!conversation && !isGalaxy && (
        <QAPair conversation={conversation} containerRef={dialogueContainerRef} />
      )}
    </div>
  );
};

export default React.forwardRef<DialogueRefObject, DialogueProps<any>>(Dialogue);
export { Message };

export * from './components/Message';

const Container = styled.div`
  position: relative;
  height: 100%;
  background: radial-gradient(circle at top, var(--fett-chat-dialogue-glow) 0%, transparent 38%),
    linear-gradient(180deg, var(--fett-chat-dialogue-bg) 0%, var(--fett-bg-app) 100%);
  overscroll-behavior: auto;
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    border-radius: 3px;
    background-color: transparent;
    opacity: 0;
  }
  &:hover::-webkit-scrollbar-thumb {
    background-color: var(--fett-scrollbar-thumb);
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;
